import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Row, Col, Space, Tooltip } from 'antd';
import FeatherIcon from 'feather-icons-react';
import { DropdownSelect } from '../dropdown-select/DropdownSelect';
import { useDictionaryTranslation } from '../../hooks/useDictionaryTranslation';
import { UsersService } from '../../services/usersService';
import { profileTypes } from '../../config/constants/userConstants';
import { solutionConstants } from '../../config/constants/solutionsConstants';
import { Button } from '../buttons/buttons';

export const SelectUser = ({
  placeholder,
  selectedUser,
  onChange,
  getData,
  multiple,
  autoselect,
  disabled,
  ...props
}) => {
  const {
    specialties,
    idProfile,
    headquarter,
    headquarters,
    excludedIds,
    allowCurrentUser,
    solutionId,
    openModalTechniciansNear,
    activeUsersOnly,
  } = props;
  const { translate: t } = useDictionaryTranslation();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);

  const selectUser = (item) => {
    if (multiple) {
      onChange(item);
      return;
    }
    let selectedOption;
    if (item) selectedOption = users.find((s) => s._id === item.value);
    onChange(selectedOption);
  };

  const getUsers = useCallback(
    async (search) => {
      if (autoselect && idProfile === profileTypes.TECHNICIAN.id && (!headquarter || !specialties)) return;

      setLoading(true);
      try {
        const { users: usersList } = await getData({
          search,
          specialties,
          idProfile,
          headquarter,
          headquarters,
          excludedIds,
          allowCurrentUser,
          status: activeUsersOnly ? true : null,
        });
        setUsers(usersList);

        if (usersList?.length === 1 && !selectedUser) {
          const selectedItem = {
            ...usersList[0],
            data: usersList[0],
            value: usersList[0]._id,
            label: usersList[0].profile.name,
          };
          if (multiple) {
            selectUser([selectedItem]);
          } else {
            selectUser(selectedItem);
          }
        }
      } catch (error) {
        setUsers([]);
      } finally {
        setLoading(false);
      }
    },
    [specialties, idProfile, headquarter, headquarters, excludedIds],
  );

  useEffect(() => {
    getUsers();
  }, [getUsers]);

  const getOptionData = useCallback((user) => {
    return {
      value: user._id,
      label: user.profile.name,
    };
  }, []);

  return (
    <Row justify="end" gutter={8}>
      <Col flex="1 1 80%">
        <DropdownSelect
          placeholderText={placeholder || t('userPlaceholder')}
          isLoading={loading}
          onChange={selectUser}
          options={users}
          value={selectedUser}
          disabled={disabled}
          onSearch={getUsers}
          getOptionData={getOptionData}
          multiple={multiple}
        />
      </Col>
      {solutionId === solutionConstants.MIEMERGENCIA.id && specialties && (
        <Space size="small">
          <Tooltip title={t('techniciansNear')}>
            <Button type="secondary" size="large" onClick={openModalTechniciansNear} disabled={disabled}>
              <FeatherIcon icon="map-pin" />
            </Button>
          </Tooltip>
        </Space>
      )}
    </Row>
  );
};

SelectUser.propTypes = {
  selectedUser: PropTypes.shape({
    _id: PropTypes.string,
    profile: PropTypes.shape({
      name: PropTypes.string,
    }),
  }),
  onChange: PropTypes.func.isRequired,
  specialties: PropTypes.arrayOf(PropTypes.string),
  idProfile: PropTypes.string,
  placeholder: PropTypes.string,
  getData: PropTypes.func,
  headquarter: PropTypes.string,
  headquarters: PropTypes.arrayOf(PropTypes.string),
  multiple: PropTypes.bool,
  autoselect: PropTypes.bool,
  excludedIds: PropTypes.arrayOf(PropTypes.string),
  allowCurrentUser: PropTypes.bool,
  solutionId: PropTypes.number,
  openModalTechniciansNear: PropTypes.func,
  activeUsersOnly: PropTypes.bool,
  disabled: PropTypes.bool,
};

SelectUser.defaultProps = {
  getData: UsersService.getAll,
};
